import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import ChevronRight from "./DropRight.svg";
import styles from "./SideNavBar.module.css";
import UpdatesTabs from "../UpdatesTabs/UpdatesTabs";
import NewFeature from "./NewFeature";
import NavInfoButton from "./NavInfoButton";

const SideNavBar = ({
                      children,
                      width,
                      navList,
                      urlBasedActiveNav = false,
                      static: isStatic = false,
                      hideToggle = false,
                      enableToggle = true,
                      defaultCollapsed = false,
                      messages = [],
                      ...rest
                    }) => {
  const activeKeyRef = useRef(null);
  const linkRefs = useRef({});
  const childrenRef = useRef(null);
  const sideNavBarRef = useRef(null);

  const location = useLocation();
  const toggleHidden = hideToggle || enableToggle === false;

  const [collapsed, setCollapsed] = useState(() => {
    if (isStatic) return false;
    return defaultCollapsed;
  });
  const [togglePosition, setTogglePosition] = useState(null);

  useLayoutEffect(() => {
    if (isStatic || toggleHidden) return;

    const updateTogglePosition = () => {
      const sideNavBar = sideNavBarRef.current;
      if (!sideNavBar) return;

      const bounds = sideNavBar.getBoundingClientRect();
      const sidebarWidth = parseFloat(
          getComputedStyle(sideNavBar).getPropertyValue("--side-nav-bar-width"),
      );

      let left = bounds.left + sidebarWidth;
      if (!collapsed) {
        left -= 1;
      }

      setTogglePosition({
        left,
        top: bounds.top + bounds.height / 2,
      });
    };

    updateTogglePosition();
    window.addEventListener("resize", updateTogglePosition);

    return () => window.removeEventListener("resize", updateTogglePosition);
  }, [collapsed, isStatic, toggleHidden, width]);

  useEffect(() => {
    if (isStatic) {
      setCollapsed(false);
    }
  }, [isStatic]);

  useEffect(() => {
    if (location.state?.isScrollSpy) {
      return;
    }
    const scrollTimer = setTimeout(() => {
      const activeNode = linkRefs.current[activeKeyRef.current];

      if (activeNode) {
        activeNode.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 50);

    return () => clearTimeout(scrollTimer);
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (isStatic || toggleHidden) return;

    window.dispatchEvent(
        new CustomEvent("side-nav-toggle", {
          detail: { collapsed },
        }),
    );
  }, [collapsed, isStatic, toggleHidden]);

  useEffect(() => {
    if (!isStatic) {
      setCollapsed(defaultCollapsed);
    }
  }, [isStatic, defaultCollapsed]);

  // on every navigation, bringing the children div to start from top so no stale scrolls are seen in the navigated page.
  useEffect(() => {
    if (location.state?.isScrollSpy) {
      return;
    }

    if (childrenRef.current) {
      childrenRef.current.scrollTop = 0;
    }
  }, [location.pathname, location.state]);

  return (
      <div
          ref={sideNavBarRef}
          className={`${styles.sideNavBarContainer} ${
              !isStatic && collapsed ? styles.collapsed : ""
          }`}
          style={{
            "--side-nav-bar-width": isStatic ? width : collapsed ? "0px" : width,
          }}
      >
        <ul
            className={[styles.sideNavBar, rest?.className].join(" ")}
            style={rest?.style}
        >
          {navList.map((navItem) => (
              <NavItem
                  key={navItem.label}
                  navItem={navItem}
                  activeKeyRef={activeKeyRef}
                  linkRefs={linkRefs}
                  urlBasedActiveNav={urlBasedActiveNav}
              />
          ))}
        </ul>
        {!isStatic &&
            !toggleHidden &&
            togglePosition &&
            createPortal(
                <SideNavToggle
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    position={togglePosition}
                />,
                document.body,
            )}
        <div
            className={styles.sideNavBarChildren}
            id="sidenavbar-children-container"
            ref={childrenRef}
        >
          {Array.isArray(messages) && messages.length > 0 && (
              <UpdatesTabs messages={messages} />
          )}
          <div className={styles.sideNavBarContent}>{children}</div>
        </div>
      </div>
  );
};

const NavItem = ({ navItem, activeKeyRef, linkRefs, urlBasedActiveNav }) => {
  const [open, setOpen] = useState(true);

  const location = useLocation();

  const getClassName = (isActive, key, subItem) => {
    let active = isActive;

    if (active) {
      activeKeyRef.current = key;

      if (urlBasedActiveNav) {
        if (navItem?.items && subItem) {
          active = location.pathname.startsWith(subItem.url);
        } else {
          active = true;
        }
      }
    }

    return active
        ? `${styles.navItem} ${styles.activeNavItem}`
        : styles.navItem;
  };

  return (
      <li
          ref={(el) => {
            if (el) linkRefs.current[navItem.label] = el;
          }}
      >
        {navItem.items ? (
            <div className={styles.subList}>
              {navItem.label && (
                  <div
                      className={styles.groupHeader}
                      onClick={() => setOpen((v) => !v)}
                  >
              <span className={styles.groupLabel}>
                {navItem.label}

                {navItem.isNew && (
                  <span style={{ marginLeft: "0.6rem" }}>
                    <NewFeature />
                  </span>
                )}
              </span>
                    <span
                        className={`${styles.dropdownIcon} ${
                            open ? styles.rotated : ""
                        }`}
                    >
                <img src={ChevronRight} alt="Toggle" width={10} height={10} />
              </span>
                  </div>
              )}

              <ul className={`${styles.sideNavList} ${open ? styles.open : ""}`}>
                {navItem.items.map((subItem) => {
                  const key = `${navItem.label}-${subItem.label}`;

                  return (
                      <li
                          key={subItem.label}
                          ref={(el) => {
                            if (el) linkRefs.current[key] = el;
                          }}
                      >
                        <div className={styles.navRow}>
                          <NavLink
                              to={subItem.url}
                              className={({ isActive }) =>
                                  getClassName(isActive, key, subItem)
                              }
                          >
                            {subItem.icon && (
                                <img
                                    src={subItem.icon}
                                    width={20}
                                    height={20}
                                    style={{ marginRight: "10px" }}
                                    alt={subItem.label}
                                />
                            )}
                            <span>{subItem.label}</span>
                            {/* FIXME: need to add the sparkling img like gif */}
                            {subItem.isNew && (
                                <div style={{ marginLeft: "0.6rem" }}>
                                  <NewFeature />
                                </div>
                            )}
                          </NavLink>

                          {subItem.info && (
                              <NavInfoButton
                                  label={subItem.label}
                                  render={subItem.info}
                              />
                          )}
                        </div>
                      </li>
                  );
                })}
              </ul>
            </div>
        ) : (
            <div className={styles.navRow}>
              <NavLink
                  to={navItem.url}
                  className={({ isActive }) => getClassName(isActive, navItem.label)}
              >
                {navItem.icon && (
                    <img
                        src={navItem.icon}
                        width={15}
                        height={15}
                        style={{ marginRight: "10px" }}
                        alt={navItem.label}
                    />
                )}
                <span>{navItem.label}</span>
                {navItem.isNew && (
                  <span style={{ marginLeft: "0.6rem" }}>
                    <NewFeature />
                  </span>
                )}
              </NavLink>

              {navItem.info && (
                  <NavInfoButton label={navItem.label} render={navItem.info} />
              )}
            </div>
        )}
      </li>
  );
};

function SideNavToggle({ collapsed, setCollapsed, position }) {
  return (
      <div
          className={styles.sideNavToggle}
          style={{ left: position.left, top: position.top }}
          onClick={() => setCollapsed((prev) => !prev)}
      >
        {collapsed ? "›" : "‹"}
      </div>
  );
}

export default SideNavBar;